import { Component } from '@theme/component';

/**
 * @typedef {object} StickyAddToCartRefs
 * @property {HTMLButtonElement} button - The sticky "Add to cart" button.
 */

/**
 * A custom element that shows a sticky "Add to cart" bar once the page's
 * primary add-to-cart button has scrolled out of view, and forwards clicks
 * to that real button so the existing add-to-cart logic (validation, cart
 * drawer, animations, etc.) is reused as-is.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCart extends Component {
  requiredRefs = ['button'];

  /** @type {HTMLButtonElement | null} */
  #target = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {MutationObserver | null} */
  #disabledObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const target = document.querySelector('#MainContent .add-to-cart-button');
    this.#target = target instanceof HTMLButtonElement ? target : null;

    if (!this.#target) {
      this.hidden = true;
      return;
    }

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.classList.toggle('sticky-add-to-cart--visible', !entry.isIntersecting);
    });
    this.#intersectionObserver.observe(this.#target);

    this.#syncDisabled();
    this.#disabledObserver = new MutationObserver(this.#syncDisabled);
    this.#disabledObserver.observe(this.#target, { attributes: true, attributeFilter: ['disabled'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#intersectionObserver?.disconnect();
    this.#disabledObserver?.disconnect();
  }

  #syncDisabled = () => {
    if (!this.#target) return;
    this.refs.button.toggleAttribute('disabled', this.#target.disabled);
  };

  /**
   * Forwards the click to the real add-to-cart button so that all of its
   * existing behavior (validation, animations, the actual cart submission)
   * runs exactly as if the user had clicked it directly.
   */
  handleClick() {
    this.#target?.click();
  }
}

if (!customElements.get('sticky-add-to-cart-component')) {
  customElements.define('sticky-add-to-cart-component', StickyAddToCart);
}
